// Binds the dynamic-form module to the Global Settings screen and registers
// `location_settings_input`, the one field kind that exists only here.

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import {
  FieldRenderer,
  messageForError,
  resolveGlobalSettingsKind,
} from '../../../features/dynamic-form';
import passwordMask from '../../../utils/passwordMask';
import EditLocationSettings from '../../../components/EditLocationSettings';
import AppJsonEditor from '../../../components/LocationSettingsForm/AppJsonEditor';

// `location_settings_input` is the one field kind that only exists on this
// screen, so it is registered as an override rather than living in the module.
const LocationSettingsField = ({
  descriptor,
  value = [],
  readOnly = true,
  handlers,
}) => (
  <EditLocationSettings
    fieldName={descriptor.name}
    definitions={Array.isArray(value) ? value : []}
    handlers={handlers}
    editDisabled={readOnly}
  />
);

LocationSettingsField.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  descriptor: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  value: PropTypes.any,
  readOnly: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  handlers: PropTypes.object.isRequired,
};
// Returns the `decideFormElement`-shaped callback the section components expect.
export default function useGlobalFieldRenderer({
  form,
  texts,
  readOnly,
  locationSettingsHandlers,
  onChange,
  onBlur,
  onHeaderValidate,
}) {
  // Field and header-row errors both live in the module's state, so they are
  // rendered from the same map. The screen's own `errors` holds the metadata,
  // execution-condition and panel messages, which are keyed separately.
  const errorMessages = useMemo(
    () =>
      Object.keys(form.errors).reduce(
        (acc, key) => ({
          ...acc,
          [key]: messageForError(form.errors[key], texts),
        }),
        {},
      ),
    [form.errors, texts],
  );

  const components = useMemo(
    () => ({
      locationSettings: (fieldProps) => (
        <LocationSettingsField
          {...fieldProps}
          handlers={locationSettingsHandlers}
        />
      ),
    }),
    [locationSettingsHandlers],
  );

  return useCallback(
    (descriptor) => (
      <FieldRenderer
        key={descriptor.name}
        descriptor={descriptor}
        kind={resolveGlobalSettingsKind(descriptor)}
        label={form.labelOf(descriptor.name)}
        value={form.values[descriptor.name]}
        warningMessage={errorMessages[descriptor.name]}
        readOnly={readOnly}
        texts={texts}
        onChange={onChange}
        onCheckboxChange={form.setCheckbox}
        onBlur={onBlur}
        headerErrors={errorMessages}
        onHeaderAdd={form.headers.add}
        onHeaderRemove={form.headers.remove}
        onHeaderChange={form.headers.change}
        onHeaderValidate={onHeaderValidate}
        jsonEditorOn={Boolean(form.jsonEditorOn[descriptor.name])}
        jsonEditorDisabled={Boolean(form.jsonEditorDisabled[descriptor.name])}
        onJsonToggle={form.toggleJsonEditor}
        onJsonDataChange={form.setJsonData}
        jsonEditor={AppJsonEditor}
        encryptText={passwordMask}
        components={components}
      />
    ),
    [
      form,
      texts,
      readOnly,
      errorMessages,
      onChange,
      onBlur,
      onHeaderValidate,
      components,
    ],
  );
}
